import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { serverModelToInputModel } from "./converter/server-model-to-input-model";
import { useSnackSettingsFormModelMethods } from "./methods";
import { excludeKeys, type DesignSnackBlocksFormModel, type ExcludeKeys, type ServerModel } from "./type";
import { snackBlocksInputZodSchema } from "./validation-schema";

import type { SnackBlocksFormModelMethodsType } from "./methods";
import type { SnackBlocksZodSchemaType } from "./validation-schema";

type Props = {
	data: ServerModel;
};

type ViewModel = DesignSnackBlocksFormModel["ViewModel"];
type InputModel = DesignSnackBlocksFormModel["InputModel"];
type QueryData = DesignSnackBlocksFormModel["QueryData"];
export type DesignSnackLayoutSettingsFormModelOnSetViewModelType = <K extends keyof ViewModel>(
	values: { key: K; value: ViewModel[K] }[],
) => void;

export type SnackLayoutSettingsFormModelType = {
	snackLayoutSettingsViewModel: DesignSnackBlocksFormModel["ViewModel"];
	resetSnackLayoutSettingsViewModel: () => void;
	snackLayoutSettingsMethods: SnackBlocksFormModelMethodsType;
};

export const useCreateSnackLayoutSettingsFormModel = ({ data }: Props): SnackLayoutSettingsFormModelType => {
	const inputModel = serverModelToInputModel({ snackData: data });

	const initialViewModel = inputModelToViewModelConverter<
		InputModel,
		ExcludeKeys,
		QueryData,
		SnackBlocksZodSchemaType
	>({
		defaultInput: inputModel,
		initInput: undefined,
		currentInput: inputModel,
		excludeKeys,
		zodSchema: snackBlocksInputZodSchema,
	});

	const [viewModel, setViewModel] = useState<ViewModel>(initialViewModel);

	const onSetViewModel: DesignSnackLayoutSettingsFormModelOnSetViewModelType = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce((acc, cur) => {
					acc[cur.key] = cur.value;
					return acc;
				}, {} as ViewModel),
			} as ViewModel;

			const convertedInputModel = viewModelToInputModelConverter<InputModel, ExcludeKeys, QueryData>(
				mergedViewModel,
			);

			const convertedViewModel = inputModelToViewModelConverter<
				InputModel,
				ExcludeKeys,
				QueryData,
				SnackBlocksZodSchemaType
			>({
				defaultInput: inputModel,
				initInput: inputModel,
				currentInput: convertedInputModel,
				excludeKeys,
				zodSchema: snackBlocksInputZodSchema,
			});

			setViewModel(convertedViewModel);
		},
		[viewModel, inputModel, setViewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	return {
		snackLayoutSettingsViewModel: viewModel,
		resetSnackLayoutSettingsViewModel: resetViewModel,
		snackLayoutSettingsMethods: {
			...useSnackSettingsFormModelMethods({ viewModel, onSetViewModel, snackList: data.snackList }),
		},
	};
};
